import { gsap } from "gsap";

// Forked from Blake Bowen's shape overlays:
// https://codepen.io/osublake/pen/BYwgBg (adapted for GSAP loader + auto control)

// Global error handler for GSAP DOM errors
gsap.config({ autoSleep: 60, force3D: false });
window.addEventListener("error", (event) => {
  if (event.message && event.message.includes("removeChild")) {
    console.warn("GSAP DOM node error prevented:", event.message);
    event.preventDefault?.();
  }
});

const OVERLAY_SELECTOR = ".shape-overlays";
const PATH_SELECTOR = ".shape-overlays__path";
const TITLE_SELECTOR = ".loader-title";

const NUM_POINTS = 10;
const DELAY_POINTS_MAX = 0.3;
const DELAY_PER_PATH = 0.25;
const DURATION = 0.9;

let overlay: HTMLElement | null;
let paths: SVGPathElement[] = [];
let title: HTMLElement | null;
let numPaths: number = 0;
let isOpened = true;
let isClosed = false;
let introPrepared = false;
let pointsDelay: number[] = [];
let allPoints: number[][] = [];
let prefersReducedMotion = false;

const tl = gsap.timeline({
  onUpdate: render,
  defaults: { ease: "power2.inOut", duration: DURATION },
});

function getIntroTargets() {
  const nav = document.querySelector(".js-navbar") as HTMLElement | null;
  const allNavItems = gsap.utils.toArray(".js-nav-item") as HTMLElement[];
  const allHeroItems = gsap.utils.toArray(".js-hero-item") as HTMLElement[];

  // Filter to only elements that are still in the DOM
  const navItems = allNavItems.filter(el => {
    try {
      return document.contains(el);
    } catch {
      return false;
    }
  });

  // Filter to only visible hero items (not hidden by display: none)
  // Check both the element and its ancestors for visibility
  const heroItems = allHeroItems.filter(el => {
    try {
      if (!document.contains(el)) return false;
      let current: HTMLElement | null = el;
      while (current && current !== document.body) {
        const style = window.getComputedStyle(current);
        if (style.display === "none") {
          return false;
        }
        current = current.parentElement;
      }
      return true;
    } catch {
      return false;
    }
  });
  return { nav, navItems, heroItems };
}

function prepIntroAnimations() {
  if (prefersReducedMotion) return;
  if (introPrepared) return;
  try {
    const { nav, navItems, heroItems } = getIntroTargets();
    const hasTargets = nav || navItems.length || heroItems.length;
    if (!hasTargets) return;

    try {
      if (nav && document.contains(nav)) {
        gsap.set(nav, { autoAlpha: 0, y: -24, filter: "blur(6px)" });
      }
    } catch (e) {
      console.warn("Error setting nav animation:", e);
    }

    try {
      if (navItems.length) {
        const activeNavItems = navItems.filter(el => document.contains(el));
        if (activeNavItems.length) {
          gsap.set(activeNavItems, { autoAlpha: 0, y: -10, filter: "blur(4px)" });
        }
      }
    } catch (e) {
      console.warn("Error setting nav items animation:", e);
    }

    try {
      if (heroItems.length) {
        const activeHeroItems = heroItems.filter(el => document.contains(el));
        if (activeHeroItems.length) {
          gsap.set(activeHeroItems, { autoAlpha: 0, y: 28, filter: "blur(8px)" });
        }
      }
    } catch (e) {
      console.warn("Error setting hero items animation:", e);
    }

    introPrepared = true;
  } catch (error) {
    console.warn("Error preparing intro animations:", error);
  }
}

function revealIntroImmediate() {
  try {
    const { nav, navItems, heroItems } = getIntroTargets();
    if (nav && document.contains(nav)) {
      gsap.set(nav, { autoAlpha: 1, y: 0, filter: "blur(0px)" });
    }
    if (navItems.length) {
      const activeNavItems = navItems.filter(el => document.contains(el));
      if (activeNavItems.length) {
        gsap.set(activeNavItems, { autoAlpha: 1, y: 0, filter: "blur(0px)" });
      }
    }
    if (heroItems.length) {
      const activeHeroItems = heroItems.filter(el => document.contains(el));
      if (activeHeroItems.length) {
        gsap.set(activeHeroItems, { autoAlpha: 1, y: 0, filter: "blur(0px)" });
      }
    }
    introPrepared = true;
  } catch (error) {
    console.warn("Error revealing intro immediately:", error);
  }
}

function playIntroAnimations() {
  if (prefersReducedMotion) {
    revealIntroImmediate();
    return;
  }
  try {
    const { nav, navItems, heroItems } = getIntroTargets();
    const tlIntro = gsap.timeline({
      defaults: { ease: "power2.out", duration: 0.85 },
    });

    if (nav && document.contains(nav)) {
      tlIntro.to(nav, { autoAlpha: 1, y: 0, filter: "blur(0px)" }, 0.05);
    }
    if (navItems.length) {
      const activeNavItems = navItems.filter(el => document.contains(el));
      if (activeNavItems.length) {
        tlIntro.to(
          activeNavItems,
          { autoAlpha: 1, y: 0, filter: "blur(0px)", stagger: 0.08 },
          0.15
        );
      }
    }
    if (heroItems.length) {
      const activeHeroItems = heroItems.filter(el => document.contains(el));
      if (activeHeroItems.length) {
        tlIntro.to(
          activeHeroItems,
          { autoAlpha: 1, y: 0, filter: "blur(0px)", stagger: 0.12 },
          0.25
        );
      }
    }
  } catch (error) {
    console.warn("Error playing intro animations:", error);
    revealIntroImmediate();
  }
}

function initPoints() {
  allPoints = [];
  for (let i = 0; i < numPaths; i += 1) {
    const points: number[] = [];
    for (let j = 0; j < NUM_POINTS; j += 1) {
      points.push(100);
    }
    allPoints.push(points);
  }
}

function render() {
  for (let i = 0; i < numPaths; i += 1) {
    const path = paths[i];
    const points = allPoints[i];
    let d = "";

    d += isOpened ? `M 0 0 V ${points[0]} C` : `M 0 ${points[0]} C`;
    for (let j = 0; j < NUM_POINTS - 1; j += 1) {
      const p = ((j + 1) / (NUM_POINTS - 1)) * 100;
      const cp = p - (100 / (NUM_POINTS - 1)) / 2;
      d += ` ${cp} ${points[j]} ${cp} ${points[j + 1]} ${p} ${points[j + 1]}`;
    }
    d += isOpened ? " V 100 H 0" : " V 0 H 0";
    path.setAttribute("d", d);
  }
}

function animateOverlay(open: boolean) {
  isOpened = open;
  tl.progress(0).clear();

  for (let i = 0; i < NUM_POINTS; i += 1) {
    pointsDelay[i] = Math.random() * DELAY_POINTS_MAX;
  }

  for (let i = 0; i < numPaths; i += 1) {
    const points = allPoints[i];
    // Cast to any to avoid GSAP type error with numeric keys
    const targets: any = points;
    const pathDelay = DELAY_PER_PATH * (open ? i : numPaths - i - 1);

    for (let j = 0; j < NUM_POINTS; j += 1) {
      const delay = pointsDelay[j] + pathDelay;
      tl.to(targets, { [j]: 0 }, delay);
    }
  }

  if (open) {
    tl.eventCallback("onComplete", null);
  } else {
    tl.eventCallback("onComplete", () => {
      if (overlay) {
        overlay.setAttribute("aria-hidden", "true");
        overlay.style.display = "none";
      }
      playIntroAnimations();
    });
  }
}

export const initOverlayLoader = () => {
  overlay = document.querySelector(OVERLAY_SELECTOR);
  if (!overlay) return;

  paths = Array.from(overlay.querySelectorAll(PATH_SELECTOR));
  if (!paths.length) return;

  title = document.querySelector(TITLE_SELECTOR);

  prefersReducedMotion =
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Check if device is mobile/touch device
  const isMobileDevice =
    window.matchMedia &&
    window.matchMedia("(hover: none), (pointer: coarse)").matches;

  numPaths = paths.length;
  isClosed = false;
  overlay.style.display = "block";
  overlay.setAttribute("aria-hidden", "false");
  if (title) {
    title.classList.remove("is-hidden");
    title.style.display = "flex";
    title.setAttribute("aria-hidden", "false");
  }

  if (prefersReducedMotion || isMobileDevice) {
    overlay.setAttribute("aria-hidden", "true");
    overlay.style.display = "none";
    if (title) {
      title.classList.add("is-hidden");
      title.style.display = "none";
      title.setAttribute("aria-hidden", "true");
    }
    revealIntroImmediate();
    isClosed = true;
    return;
  }

  prepIntroAnimations();

  initPoints();
  render();
  animateOverlay(true);
};

export const closeOverlayLoader = () => {
  if (!overlay || isClosed) return;
  isClosed = true;

  if (title) {
    gsap.to(title, {
      opacity: 0,
      duration: 0.4,
      ease: "power2.out",
      onComplete: () => {
        if (title) {
          title.classList.add("is-hidden");
          title.style.display = "none";
          title.setAttribute("aria-hidden", "true");
        }
      },
    });
  }

  prepIntroAnimations();

  initPoints();
  render();
  animateOverlay(false);
};
